import React, { useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import Icon1 from '../../assets/img/logo/favicon.png';
import {Message, Whatsapp, UserEdit, MessageSearch, Call} from 'iconsax-react';
import CTAButton from '../../components/CTA/CTAButton';
import { Helmet } from "react-helmet-async";
import ReCAPTCHA from "react-google-recaptcha";
import { toast } from 'react-toastify';
import { contactFeedback } from '../../actions';

const RECAPTCHA_SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY;

function Contact() {
  const captchaRef = useRef(null);
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    subject: '',
    message: '',
  });

  const [errors, setErrors] = useState({});
  const [captchaValue, setCaptchaValue] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Handle input change
  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  // Validate form fields
  const validateForm = () => {
    let newErrors = {};
    if (!formData.fullName.trim()) newErrors.fullName = 'Full Name is required';
    if (!formData.email.trim()) newErrors.email = 'Email is required';
    // if (!formData.phone.trim()) newErrors.phone = 'Phone number is required';
    if (!formData.subject.trim()) newErrors.subject = 'Subject is required';
    if (!formData.message.trim()) newErrors.message = 'Message is required';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleCaptchaChange = (value) => {
    setCaptchaValue(value);
    if (value) {
      setErrors((currentErrors) => {
        const { captcha, ...remainingErrors } = currentErrors;
        return remainingErrors;
      });
    }
  };

  const resetCaptcha = () => {
    captchaRef.current?.reset();
    setCaptchaValue(null);
  };

  const handleCaptchaExpired = () => {
    resetCaptcha();
    setErrors((currentErrors) => ({
      ...currentErrors,
      captcha: 'The verification expired. Please verify that you are not a robot again.',
    }));
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    if (!captchaValue) {
      setErrors({ captcha: 'Please verify that you are not a robot.' });
      return;
    }
    const credentials = {
      name: formData.fullName,
      email: formData.email,
      // phone: formData.phone,
      subject: formData.subject,
      body: formData.message,
      "g-recaptcha-response": captchaValue,
    };
    
    setIsSubmitting(true);
    try {
      const response = await contactFeedback(credentials);
      toast.success(response?.data?.message || 'Your message has been submitted successfully!');
      
      // Clear form after submission
      setFormData({
        fullName: '',
        email: '',
        // phone: '',
        subject: '',
        message: '',
      });
    } catch (error) {
      const captchaError =
        error?.response?.data?.errors?.['g-recaptcha-response']?.[0];

      if (captchaError) {
        setErrors((currentErrors) => ({
          ...currentErrors,
          captcha: 'Captcha verification failed. Please complete it again and resubmit.',
        }));
      }

      toast.error(captchaError || error?.response?.data?.message || 'An error occurred while submitting the form. Please try again later.');
      console.error('Error submitting form:', error);
    } finally {
      // reCAPTCHA tokens expire quickly and can only be verified once.
      resetCaptcha();
      setIsSubmitting(false);
    }
  };

  // remove errors after 5secs if there is an error
  useEffect(() => {
    if (Object.keys(errors).length > 0) {
      const timer = setTimeout(() => {
        setErrors({});
      }, 10000); // 10 seconds
      return () => clearTimeout(timer);
    }
  }, [errors]);
  
  return (
    <>
    
      <Helmet>
        <title>Contact Us | Cardplot</title>
        <meta
          name="description"
          content="Contact Cardplot’s support and sales team for questions about cross-border payments, virtual cards, virtual accounts, and our fintech services."
        />
        <meta
          name="keywords"
          content="Contact Cardplot, Cardplot support, Cardplot sales, help with cross-border payments, virtual cards support, African fintech contact"
        />
        <meta property="og:title" content="Contact Us | Cardplot" />
        <meta
          property="og:description"
          content="Reach out to Cardplot for assistance, inquiries, or partnership opportunities across our payment and financial services."
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://cardplot.com/contact" />
        <meta property="og:image" content="https://cardplot.com/favicon.png" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Contact Us | Cardplot" />
        <meta
          name="twitter:description"
          content="Get in touch with Cardplot’s team for support and information about our cross-border payment solutions."
        />
      </Helmet>
      <section className='breadcrumb__section bg-overlay'>
        <div className="container">
          <div className="row">
            <div className="col-lg-12">
              <div className="breadcrumb__text">
                <h3 className='breadcrumb__text__head heading'> Contact Support </h3>
                <p className='breadcrumb__text__content size'>Get in Touch – We're Here to Assist You Anytime!</p>
              </div>
              <ul className='breadcrumb__list breadcrumb__listing flex-wrap d-flex justify-content-center gap-3'>
                <li className='breadcrumb__list__item'>
                  <Link to='/' className='text-decoration-none breadcrumb__list__link'>
                    <span><Message /></span>
                    support@cardplot.com
                  </Link>
                </li>
                <li className='breadcrumb__list__item'>
                  <Link to='' className='text-decoration-none'>
                    <span><Message /></span>
                    sales@cardplot.com
                  </Link>
                </li>
                {/* <li className='breadcrumb__list__item'>
                  <a
                    href={`https://wa.me/+447347949022`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className='text-decoration-none'
                  >
                    <span><Whatsapp /></span>
                    +44 7347 949022
                  </a>
                </li> */}
              </ul>
            </div>
          </div>
        </div>
      </section>

      <section className='contact__section  mt-5'>
        <div className='container'>
          <div className='row w-half align-items-center justify-content-center'>
            <div className='col-12'>
              <div className='contact__section__info'>
                <h2 className='contact__section__info__title'>Looking for a fast reply?</h2>
                <div className='contact__section__info__button'>
                  <Link to='/help' className='btn btn--primary'>Visit Help Centre</Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>


      <section className='contact__section padding__tb__100'>
        <div className='container'>
        <div className='row w-half align-items-center justify-content-center'>
            <div className='col-12'>
              <div className='contact__section__content'>
                <h2 className='contact__section__form__title'>Send us a message!</h2>
              </div>
              <form onSubmit={handleSubmit} className='contact__form'>
                <div className='form-group mb-3'>
                  <label>Full Name</label>
                  <div className='input-group'>
                    <UserEdit className='contact__form__icon' />
                    <input 
                      type="text" 
                      name="fullName" 
                      value={formData.fullName} 
                      onChange={handleChange} 
                      className="" 
                    />
                  </div>
                  {errors.fullName && <small className="text-danger">{errors.fullName}</small>}
                </div>

                <div className='form-group mb-3'>
                  <label>Email</label>
                  <div className='input-group'>
                    <MessageSearch className='contact__form__icon' />
                    <input 
                      type="email" 
                      name="email" 
                      value={formData.email} 
                      onChange={handleChange} 
                      className="" 
                    />
                  </div>
                  {errors.email && <small className="text-danger">{errors.email}</small>}
                </div>

                {/* <div className='form-group mb-3'>
                  <label>Phone number</label>
                  <div className='input-group'>
                    <Call className='contact__form__icon' />
                    <input 
                      type="text" 
                      name="phone" 
                      value={formData.phone} 
                      onChange={handleChange} 
                      className="" 
                    />
                  </div>
                  {errors.phone && <small className="text-danger">{errors.phone}</small>}
                </div> */}

                <div className='form-group mb-3'>
                  <label>Subject</label>
                  <div className='input-group'>
                    <input 
                      type="text" 
                      name="subject" 
                      value={formData.subject} 
                      onChange={handleChange} 
                      className="" 
                    />
                  </div>
                  {errors.subject && <small className="text-danger">{errors.subject}</small>}
                </div>

                <div className='form-group mb-3'>
                  <label className='mb-2'>Message</label>
                  <div className='input-group '>
                    <textarea 
                      name="message" 
                      value={formData.message} 
                      onChange={handleChange} 
                      rows={5} 
                      className="" 
                    />
                  </div>
                  {errors.message && <small className="text-danger top-0">{errors.message}</small>}
                </div>

                <div className="d-flex flex-column justify-content-center align-items-center">
                  <ReCAPTCHA
                    ref={captchaRef}
                    sitekey={RECAPTCHA_SITE_KEY}
                    onChange={handleCaptchaChange}
                    onExpired={handleCaptchaExpired}
                    onErrored={() => {
                      resetCaptcha();
                      setErrors((currentErrors) => ({
                        ...currentErrors,
                        captcha: 'Captcha could not load. Please check your connection and try again.',
                      }));
                    }}
                  />
                  {errors.captcha && <p className="error-text text-danger">{errors.captcha}</p>}
                </div>

                <div className='form-group pt-4 d-flex flex-column justify-content-center align-items-center'>
                  <button type='submit' className='btn btn--primary w-50 py-4 rounded-pill' disabled={isSubmitting}>
                    {isSubmitting ? 'Submitting...' : 'Submit'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </section>

      <CTAButton />
    </>
  )
}

export default Contact
